import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ChevronRight from '@elliemae/ds-icons/ChevronRight';
import ChevronLeft from '@elliemae/ds-icons/ChevronLeft';
import styled from 'styled-components';
import DSButton from '@elliemae/ds-button';
import { useResize } from './useResize.js';
import 'lodash';

var _templateObject;
var CarouselBtn = /*#__PURE__*/styled(DSButton).withConfig({
  componentId: "sc-1317h31-0"
})(["position:absolute;z-index:1;margin:0;&:focus{& span{background-color:", ";background-size:90%;border-radius:2px;border-color:transparent;}}&&{width:16px;height:calc(100% - 2px);", "}", " && .em-ds-icon{height:16px;width:16px;}"], function (props) {
  return props.theme.colors.brand[200];
}, function (props) {
  return props.theme.media.medium(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    height: calc(100% - 4px);\n    &:focus {\n    & span {\n      background-color: unset;\n    }\n  }\n    "])));
}, function (props) {
  return props.right ? 'right: 0;' : 'left: 0;';
});
var CarouselWrapper = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-1317h31-1"
})(["position:relative;padding:0 16px;width:100%;"]);
var ChildrenWrap = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-1317h31-2"
})(["display:flex;flex-wrap:nowrap;flex:1;overflow:hidden;"]);

function isVisibleElement(el, container) {
  var rect = el.getBoundingClientRect();
  var cRect = container.getBoundingClientRect();
  var elemLeft = rect.left + 3;
  var elemRight = rect.right - 3; // Only completely visible elements return true:

  var isVisible = elemLeft >= cRect.left && elemRight <= cRect.right;
  return isVisible;
}

var Carousel = function Carousel(_ref) {
  var children = _ref.children,
      onScroll = _ref.onScroll;
  var itemsRef = useRef([]);
  var wrapRef = useRef(null);
  var leftRef = useRef(null);
  var rightRef = useRef(null);

  var shouldShowLeft = function shouldShowLeft() {
    return children && children[0] && wrapRef.current && itemsRef.current[0] && !isVisibleElement(itemsRef.current[0], wrapRef.current);
  };

  var shouldShowRight = function shouldShowRight() {
    return children && children[children.length - 1] && wrapRef.current && itemsRef.current[children.length - 1] && !isVisibleElement(itemsRef.current[children.length - 1], wrapRef.current);
  };

  var _useState = useState({
    right: false,
    left: false,
    CHEVRON_FOCUS_FLAG: false // flag to replace lack of callback in useState hook

  }),
      _useState2 = _slicedToArray(_useState, 2),
      _useState2$ = _useState2[0],
      right = _useState2$.right,
      left = _useState2$.left,
      CHEVRON_FOCUS_FLAG = _useState2$.CHEVRON_FOCUS_FLAG,
      setShow = _useState2[1];

  var recalcChevrons = function recalcChevrons() {
    return setShow({
      right: shouldShowRight(),
      left: shouldShowLeft()
    });
  };

  useEffect(function () {
    recalcChevrons();
  }, []);
  useEffect(function () {
    if (CHEVRON_FOCUS_FLAG) {
      // focus opposite chevron when current one hides
      if (!left && right && rightRef.current) {
        rightRef.current.focus();
      } else if (!right && left && leftRef.current) {
        leftRef.current.focus();
      }

      setShow({
        right: right,
        left: left,
        CHEVRON_FOCUS_FLAG: false
      });
    }
  }, [CHEVRON_FOCUS_FLAG]);
  useResize(wrapRef, recalcChevrons);

  var getVisibleItemsOffset = function getVisibleItemsOffset() {
    return itemsRef.current.reduce(function (acc, c) {
      if (isVisibleElement(c, wrapRef.current)) {
        var styles = window.getComputedStyle(c);
        var width = c.offsetWidth;
        var margin = parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
        return acc + (width + margin);
      }

      return acc;
    }, 0);
  };

  var rightClick = function rightClick() {
    wrapRef.current.scrollLeft += getVisibleItemsOffset();
  };

  var leftClick = function leftClick() {
    wrapRef.current.scrollLeft -= getVisibleItemsOffset();
  };

  var handleScroll = function handleScroll(e) {
    // forces render when element.scrollTop is changed (out of react flow)
    setShow({
      right: shouldShowRight(),
      left: shouldShowLeft(),
      // to trigger focus logic at effect above
      CHEVRON_FOCUS_FLAG: true
    });
    onScroll(e);
  };

  var handleClick = function handleClick(e) {
    var onClick = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function () {};
    var tabListDomEl = wrapRef.current;
    var el = e.target;
    var rect = el.getBoundingClientRect();
    var cRect = tabListDomEl.getBoundingClientRect();

    if (!isVisibleElement(el, tabListDomEl)) {
      var containerWidth = cRect.width;
      var halfContainer = containerWidth / 2;
      var elementScrollPosition = rect.left - cRect.left;
      var positionInContainer = elementScrollPosition % containerWidth;
      var width = el.offsetWidth; // align to right

      if (positionInContainer > halfContainer) {
        wrapRef.current.scrollLeft += width + positionInContainer - containerWidth;
      } else {
        // align left
        wrapRef.current.scrollLeft += positionInContainer;
      }

      recalcChevrons();
    }

    onClick(e);
  };

  return /*#__PURE__*/React.createElement(CarouselWrapper, null, left && /*#__PURE__*/React.createElement(CarouselBtn, {
    "data-testid": "chevron-left",
    buttonType: "link",
    innerRef: leftRef,
    size: "s",
    icon: /*#__PURE__*/React.createElement(ChevronLeft, {
      size: "s",
      color: ['brand-primary', '600']
    }),
    onClick: leftClick,
    tabIndex: -1
  }), right && /*#__PURE__*/React.createElement(CarouselBtn, {
    "data-testid": "chevron-right",
    innerRef: rightRef,
    right: true,
    size: "s",
    buttonType: "link",
    icon: /*#__PURE__*/React.createElement(ChevronRight, {
      size: "s",
      color: ['brand-primary', '600']
    }),
    onClick: rightClick,
    tabIndex: -1
  }), /*#__PURE__*/React.createElement(ChildrenWrap, {
    onScroll: handleScroll,
    ref: wrapRef
  }, React.Children.map(children, function (child, idx) {
    return /*#__PURE__*/React.cloneElement(child, {
      onClick: function onClick(e) {
        var _child$props;

        return handleClick(e, (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.onClick);
      },
      key: 'carousel'.concat(idx),
      ref: function ref(el) {
        itemsRef.current[idx] = el; // keep old refs

        if (typeof child.ref === 'function') {
          child.ref(el);
        }
      }
    });
  })));
};

Carousel.defaultProps = {
  onScroll: function onScroll() {
    return null;
  }
};
Carousel.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  onScroll: PropTypes.func
};

export default Carousel;
//# sourceMappingURL=Carousel.js.map
